/**
 * Organization model representing a company or business entity
 */
export interface Organization {
  /** Unique identifier for the organization */
  id: string;

  /** Organization name */
  name: string;

  /** Legal business name */
  legalName?: string;

  /** Tax identification number */
  taxId?: string;

  /** Email address */
  email?: string;

  /** Phone number */
  phone?: string;

  /** Organization address */
  address?: {
    street1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };

  /** Bank details */
  bankDetails?: {
    bankName?: string;
    bankAccount?: string;
    accountHolder?: string;
    bankBranch?: string;
    swiftCode?: string;
  };

  /** Logo URL */
  logo?: string;

  /** Organization settings */
  settings?: Record<string, any>;

  /** Google Sheets integration configuration */
  googleSheetsIntegration?: {
    enabled: boolean;
    spreadsheetId: string;
    spreadsheetName: string;
    spreadsheetUrl: string;
    connectedBy: string;
    connectedAt: Date | string;
    lastSyncAt?: Date | string;
    syncSettings: {
      invoices: boolean;
      suppliers: boolean;
      inventory: boolean;
    };
  };

  /** Organization members */
  members?: OrganizationMember[];

  /** Subscription details */
  subscription?: {
    plan: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    trialEnd?: string;
  };

  /** Creation timestamp */
  createdAt: string;

  /** Last update timestamp */
  updatedAt?: string;

  /** User ID who created the organization */
  createdBy: string;

  /** Owner ID */
  ownerId?: string;

  /** Flag indicating if the organization is deleted */
  isDeleted?: boolean;

  /** Flag indicating if the organization is active */
  isActive?: boolean;

  /** Contact information */
  contact?: ContactInfo;

  /** Member IDs */
  memberIds?: string[];

  /** Enterprise structure for budget management and approval workflows */
  enterpriseStructure?: {
    departments?: OrganizationDepartment[];
    projects?: OrganizationProject[];
    costCenters?: OrganizationCostCenter[];
    locations?: OrganizationLocation[];
  };
}

/**
 * Organization creation form data
 */
export interface OrganizationFormData {
  /** Organization name */
  name: string;

  /** Organization legal name */
  legalName?: string;

  /** Tax ID */
  taxId?: string;

  /** Address */
  address?: Address;

  /** Bank details */
  bankDetails?: {
    bankName?: string;
    bankAccount?: string;
    accountHolder?: string;
    bankBranch?: string;
    swiftCode?: string;
  };

  /** Contact information */
  contact?: ContactInfo;

  /** Initial organization settings */
  settings?: Partial<OrganizationSettings>;

  /** Google Sheets integration (for updates) */
  googleSheetsIntegration?: {
    enabled: boolean;
    spreadsheetId: string;
    spreadsheetName: string;
    spreadsheetUrl: string;
    connectedBy: string;
    connectedAt: Date | string;
    lastSyncAt?: Date | string;
    syncSettings: {
      invoices: boolean;
      suppliers: boolean;
      inventory: boolean;
    };
  } | null;

  /** Dynamic field updates */
  [key: string]: any;
}

/**
 * Address model
 */
export interface Address {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Contact information model
 */
export interface ContactInfo {
  email?: string;
  phone?: string;
  website?: string;
}

/**
 * Organization settings
 */
export interface OrganizationSettings {
  defaultCurrency: string;
  defaultTaxRate: number;
  defaultLanguage: string;
  fiscalYearStartMonth: number;
  timezone: string;
  dateFormat: string;
  invoiceNumberFormat: string;
  defaultInvoiceDueDays: number;
  privacySettings: {
    allowMemberDataSharing: boolean;
    allowAnonymousStatistics: boolean;
  };
  notificationSettings: {
    invoiceCreated: boolean;
    paymentReceived: boolean;
    paymentOverdue: boolean;
    subscriptionRenewal: boolean;
    memberActivity: boolean;
    lowStockAlerts?: boolean;
    stockMovements?: boolean;
  };
  featureFlags?: {
    invoiceExtraction?: boolean;
    supplierManagement?: boolean;
    inventoryManagement?: boolean;
  };
  googleSheetsSync?: {
    enabled: boolean;
    spreadsheetId: string;
    syncTables: {
      invoices: boolean;
      suppliers: boolean;
      inventory: boolean;
    };
    lastSyncAt?: string;
    syncStatus?: 'active' | 'error' | 'paused';
    errorMessage?: string;
  };
  inventorySettings?: {
    defaultLocationId?: string;
    requireApprovalForTransfers: boolean;
    requireApprovalForAdjustments: boolean;
    approvalThresholdAmount?: number;
    enableLowStockAlerts: boolean;
    lowStockCheckFrequency: number;
    autoCreateItemsFromInvoices: boolean;
    defaultUnits: string[];
    customUnits: string[];
  };
}

/**
 * Organization member role
 */
export enum OrganizationRole {
  /** Owner with full access */
  OWNER = 'owner',

  /** Admin with management access */
  ADMIN = 'admin',

  /** Regular member with limited access */
  MEMBER = 'member',

  /** Read-only access */
  VIEWER = 'viewer',

  /** Department manager with approval authority */
  DEPARTMENT_MANAGER = 'department_manager',

  /** Project manager with project-specific authority */
  PROJECT_MANAGER = 'project_manager',

  /** Finance manager with budget oversight */
  FINANCE_MANAGER = 'finance_manager',

  /** Procurement specialist with purchase authority */
  PROCUREMENT_SPECIALIST = 'procurement_specialist',

  /** Quality control inspector */
  QUALITY_INSPECTOR = 'quality_inspector',

  /** Inventory manager */
  INVENTORY_MANAGER = 'inventory_manager',
}

/**
 * Subscription plan types
 */
export enum SubscriptionPlan {
  /** Free tier with limited features */
  FREE = 'free',

  /** Basic paid tier */
  BASIC = 'basic',

  /** Business tier with more features */
  BUSINESS = 'business',

  /** Enterprise tier with all features */
  ENTERPRISE = 'enterprise',
}

/**
 * Subscription status types
 */
export enum SubscriptionStatus {
  /** Active subscription */
  ACTIVE = 'active',

  /** Trial period */
  TRIAL = 'trial',

  /** Subscription is past due */
  PAST_DUE = 'past_due',

  /** Subscription has been cancelled */
  CANCELLED = 'cancelled',

  /** Subscription has expired */
  EXPIRED = 'expired',
}

/**
 * Organization member model
 */
export interface OrganizationMember {
  /** Membership ID */
  id: string;

  /** User ID */
  userId: string;

  /** Organization ID */
  organizationId: string;

  /** User's role in the organization */
  role: OrganizationRole;

  /** When they joined the organization */
  joinedAt: string;

  /** Whether this is the user's primary organization */
  isPrimary: boolean;

  /** Who invited them */
  invitedBy?: string;

  /** User details */
  user: {
    id: string;
    displayName?: string;
    email?: string;
    photoURL?: string;
  };

  /** Flag indicating if this is the current user */
  isCurrentUser?: boolean;
}

/**
 * Organization invitation model
 */
export interface OrganizationInvitation {
  /** Unique invitation ID */
  id: string;

  /** Organization ID */
  organizationId: string;

  /** Email of the invited user */
  email: string;

  /** Assigned role */
  role: OrganizationRole;

  /** User ID who sent the invitation */
  invitedBy: string;

  /** When the invitation was created */
  createdAt: string;

  /** When the invitation expires */
  expiresAt: string;

  /** Invitation status */
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

/**
 * Organization department for enterprise structure
 */
export interface OrganizationDepartment {
  /** Unique department ID */
  id: string;

  /** Department name */
  name: string;

  /** Department code for budgeting */
  code: string;

  /** Department description */
  description?: string;

  /** Parent department ID for hierarchy */
  parentId?: string;

  /** Department manager user ID */
  managerId?: string;

  /** Manager details */
  manager?: {
    id: string;
    name: string;
    email: string;
  };

  /** Budget allocation for this department */
  budgetCode?: string;

  /** Active status */
  isActive: boolean;

  /** Creation details */
  createdAt: string;
  createdBy: string;
}

/**
 * Organization project for enterprise structure
 */
export interface OrganizationProject {
  /** Unique project ID */
  id: string;

  /** Project name */
  name: string;

  /** Project code for budgeting */
  code: string;

  /** Project description */
  description?: string;

  /** Project manager user ID */
  managerId?: string;

  /** Manager details */
  manager?: {
    id: string;
    name: string;
    email: string;
  };

  /** Associated department ID */
  departmentId?: string;

  /** Project budget allocation */
  budgetCode?: string;

  /** Project status */
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

  /** Project dates */
  startDate?: string;
  endDate?: string;

  /** Creation details */
  createdAt: string;
  createdBy: string;
}

/**
 * Organization cost center for enterprise structure
 */
export interface OrganizationCostCenter {
  /** Unique cost center ID */
  id: string;

  /** Cost center name */
  name: string;

  /** Cost center code for budgeting */
  code: string;

  /** Cost center description */
  description?: string;

  /** Responsible manager user ID */
  managerId?: string;

  /** Manager details */
  manager?: {
    id: string;
    name: string;
    email: string;
  };

  /** Associated department ID */
  departmentId?: string;

  /** Budget allocation */
  budgetCode?: string;

  /** Active status */
  isActive: boolean;

  /** Creation details */
  createdAt: string;
  createdBy: string;
}

/**
 * Organization location for enterprise structure
 */
export interface OrganizationLocation {
  /** Unique location ID */
  id: string;

  /** Location name */
  name: string;

  /** Location code for budgeting */
  code: string;

  /** Location address */
  address?: Address;

  /** Location manager user ID */
  managerId?: string;

  /** Manager details */
  manager?: {
    id: string;
    name: string;
    email: string;
  };

  /** Budget allocation */
  budgetCode?: string;

  /** Active status */
  isActive: boolean;

  /** Creation details */
  createdAt: string;
  createdBy: string;
}
